import type { FieldPath, FieldPathValue, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";

import type { CheckboxProps } from "@/design-system/Checkbox";
import { Checkbox } from "@/design-system/Checkbox";
import { FormControlGroup } from "@/design-system/Form";

interface FormCheckboxProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<CheckboxProps, "checked"> {
  "data-testid"?: string;
  name: TName;
  defaultValue?: FieldPathValue<TFormValues, TName>;
  label?: string;
  description?: string;
  rules?: RegisterOptions<TFormValues, TName>;
  memorise?: boolean;
}

export function FormCheckbox<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  "data-testid": dataTestId,
  name,
  rules,
  defaultValue,
  memorise,
  shape,
  alignTop,
  label,
  description,
  ...props
}: FormCheckboxProps<TFormValues, TName>): React.ReactNode {
  const { field } = useController<TFormValues, TName>({ name, rules, defaultValue });

  const checkbox = (
    <Checkbox
      data-testid={dataTestId}
      {...field}
      {...props}
      onChange={(e) => {
        field.onChange(e);
        props.onChange?.(e);
      }}
      name={name}
      checked={!!field.value}
      shape="square"
      tooltip={props.tooltip}
    />
  );

  if (!label) {
    return checkbox;
  }

  return (
    <Checkbox
      data-testid={dataTestId}
      {...field}
      {...props}
      onChange={(e) => {
        field.onChange(e);
        props.onChange?.(e);
      }}
      name={name}
      checked={!!field.value}
      shape="square"
      label={label}
      description={description}
      alignTop={alignTop}
      tooltip={props.tooltip}
    />
  );
}

type FormCheckboxGroupProps = {
  children: React.ReactNode;
  layout?: "horizontal" | "vertical";
};

export function FormCheckboxGroup({ children, layout = "horizontal" }: FormCheckboxGroupProps): React.ReactNode {
  return <FormControlGroup layout={layout}>{children}</FormControlGroup>;
}
